import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { Fiscal, FiscalCreateDto, FiscalUpdateDto } from 'src/typings';
import { FiscalService } from '../fiscal.service';

type FiscalSettingsDialogData = {
  fiscal?: Fiscal | null;
};

@Component({
  selector: 'app-fiscal-settings-dialog',
  templateUrl: './settings-dialog.component.html',
  styleUrls: ['./settings-dialog.component.scss']
})
export class FiscalSettingsDialogComponent {
  saving = false;

  form: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl('', Validators.required),
    address: new UntypedFormControl(''),
    sellerDisplayName: new UntypedFormControl(''),
    unitDisplayName: new UntypedFormControl(''),
    displayTaxIdLabel: new UntypedFormControl(''),
    displayTaxIdValue: new UntypedFormControl(''),
    fullAddress: new UntypedFormControl(''),
    prroFiscalNumber: new UntypedFormControl(''),
    printStaticTaxLine: new UntypedFormControl(false),
    staticTaxLabel: new UntypedFormControl(''),
    staticTaxLetter: new UntypedFormControl(''),
    isTestMode: new UntypedFormControl(false),
    printLogoOnFiscalReceipt: new UntypedFormControl(false)
  });

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly fiscalService: FiscalService,
    private readonly notificationService: NotificationService,
    public readonly dialogRef: MatDialogRef<FiscalSettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: FiscalSettingsDialogData
  ) {
    const fiscal = data?.fiscal;
    if (fiscal) {
      this.form.patchValue({
        name: fiscal.name || '',
        address: fiscal.address || '',
        sellerDisplayName: fiscal.sellerDisplayName || '',
        unitDisplayName: fiscal.unitDisplayName || '',
        displayTaxIdLabel: fiscal.displayTaxIdLabel || '',
        displayTaxIdValue: fiscal.displayTaxIdValue || '',
        fullAddress: fiscal.fullAddress || '',
        prroFiscalNumber: fiscal.prroFiscalNumber || '',
        printStaticTaxLine: Boolean(fiscal.printStaticTaxLine),
        staticTaxLabel: fiscal.staticTaxLabel || '',
        staticTaxLetter: fiscal.staticTaxLetter || '',
        isTestMode: Boolean(fiscal.isTestMode),
        printLogoOnFiscalReceipt: Boolean(fiscal.printLogoOnFiscalReceipt)
      });
    }
  }

  get isEditMode(): boolean {
    return Boolean(this.data?.fiscal?.id);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const raw = this.form.getRawValue();
    const payloadBase = {
      name: raw.name?.trim(),
      address: raw.address?.trim() || '',
      sellerDisplayName: raw.sellerDisplayName?.trim() || '',
      unitDisplayName: raw.unitDisplayName?.trim() || '',
      displayTaxIdLabel: raw.displayTaxIdLabel?.trim() || '',
      displayTaxIdValue: raw.displayTaxIdValue?.trim() || '',
      fullAddress: raw.fullAddress?.trim() || '',
      prroFiscalNumber: raw.prroFiscalNumber?.trim() || '',
      printStaticTaxLine: Boolean(raw.printStaticTaxLine),
      staticTaxLabel: raw.staticTaxLabel?.trim() || '',
      staticTaxLetter: raw.staticTaxLetter?.trim() || '',
      isTestMode: Boolean(raw.isTestMode),
      printLogoOnFiscalReceipt: Boolean(raw.printLogoOnFiscalReceipt)
    };

    const request$ = this.isEditMode
      ? this.fiscalService.update({
          ...(payloadBase as FiscalUpdateDto),
          id: Number(this.data?.fiscal?.id)
        })
      : this.fiscalService.create(payloadBase as FiscalCreateDto);

    request$.subscribe({
      next: (fiscal) => {
        this.saving = false;
        this.notificationService.notice(this.isEditMode ? 'Касса обновлена' : 'Касса создана');
        this.dialogRef.close(fiscal);
      },
      error: (error) => {
        console.error(error);
        this.saving = false;
        this.notificationService.showError('Не удалось сохранить настройки кассы');
      }
    });
  }
}
